/**
 * Single source of truth for every CHECK-constraint enumeration in the Motoveo schema
 * (architecture.md §3). These `as const` tuples are consumed by:
 *   - packages/shared    → Zod schemas (`z.enum(...)`)
 *   - packages/core/db   → Kysely column union types
 * so the DB CHECK, the runtime validation, and the TypeScript types can never drift.
 */
export const OPERATOR_ROLES = ['owner', 'operator', 'viewer'];
export const DEALER_STATUSES = ['prospect', 'active', 'paused', 'offboarded'];
/**
 * M9 dealer-recruitment funnel. ORTHOGONAL to `DEALER_STATUSES`: `pipeline_stage` tracks the
 * founder's SALES progress; `status` is the operational lifecycle that gates matching/blasts.
 * The matcher reads `status` (paused/offboarded excluded), never `pipeline_stage`.
 */
export const PIPELINE_STAGES = [
    'not_contacted',
    'contacted',
    'interested',
    'opted_in',
    'live_iso',
    'declined',
    'dormant',
];
/** How a dealer prefers to be reached on the recruitment call cadence (ops metadata; not a send gate). */
export const PREFERRED_CONTACT_METHODS = ['phone', 'email', 'text'];
/** A logged founder touch — the call-driven workflow the launch council wants over passive signups. */
export const INTERACTION_TYPES = ['call', 'email', 'text', 'note'];
/** Structured outcome of a founder touch (so pass reasons live in data, not in call memory). */
export const INTERACTION_OUTCOMES = [
    'connected',
    'left_voicemail',
    'no_answer',
    'interested',
    'not_interested',
    'opted_in',
    'captured_iso',
    'callback_requested',
    'bid_committed',
    'other',
];
export const PAINT_TO_SAMPLE_PREFS = ['required', 'preferred', 'exclude', 'any'];
export const SUBMISSION_SOURCES = ['sms', 'web_form', 'email', 'operator_manual'];
export const CONDITION_GRADES = ['concours', 'excellent', 'good', 'driver', 'fair'];
export const TITLE_STATUSES = ['clean', 'salvage', 'rebuilt', 'branded', 'lien', 'unknown'];
// --- M10 seller qualification + launch gate --------------------------------
/** What the seller is optimizing for — the council's "max price / discretion / speed / certainty". */
export const PRICE_PRIORITIES = ['max_price', 'discretion', 'speed', 'certainty'];
/** How anchored the seller is to retail (BaT/C&B) comps — 'high' is the biggest close risk. */
export const RETAIL_EXPECTATION_RISKS = ['low', 'medium', 'high'];
/** Ownership/payoff attestation for the launch-trust gate (title-TRANSFER stays a Tier-3 concern). */
export const LIEN_STATUSES = ['none', 'payoff_known', 'payoff_unknown', 'lease', 'unknown'];
/** The seven derived packet-completeness checklist items (the council's launch-readiness checklist). */
export const PACKET_CHECK_KEYS = [
    'spec_confident',
    'photos',
    'window_sticker',
    'title_lien_known',
    'condition_captured',
    'seller_consent',
    'seller_expectation',
];
/**
 * HARD launch-gate blockers (each maps to a required-ALL condition; any present ⇒ blocked unless an
 * audited operator override is supplied). The gate is a real guard on blasts, not advisory.
 *
 * Refined model (2026-07-07) — coverage semantics: blasts BROADCAST to the whole active franchise
 * network, so the two coverage codes now measure the NETWORK, not ISO matching (ISO is an optional
 * priority signal). The code values are unchanged (no stored-value migration needed):
 *   - insufficient_dealer_coverage  fewer than K active FRANCHISE dealers are reachable
 *                                   (consented + unsuppressed) — a broadcast would be uncompetitive.
 *   - no_reachable_dealers          active franchise dealers exist but ZERO are reachable — the
 *                                   broadcast would reach nobody (marketplace theater; M14/L2).
 */
export const LAUNCH_BLOCKER_CODES = [
    'missing_consent',
    'incomplete_packet',
    'insufficient_dealer_coverage',
    'price_unrealistic',
    'no_reachable_dealers',
];
/** SOFT launch-gate warnings — surfaced, never blocking (price not yet estimable, mis-anchor risk, …). */
export const LAUNCH_WARNING_CODES = [
    'price_unverified',
    'price_no_anchor',
    'price_marginal',
    'seller_retail_anchored',
    'seller_floor_missing',
    // retained for old recorded launch-gate snapshots (pre-M14 blasts.match_criteria_snapshot);
    // since M14/L2 the LIVE gate emits this as a BLOCKER, never a warning.
    'no_reachable_dealers',
];
/** Price-realism verdict: seller anchor vs. the (injected/estimated) plausible dealer-bid band. */
export const PRICE_REALISM_STATUSES = ['ok', 'marginal', 'above_band', 'no_anchor', 'no_estimate'];
export const SUBMISSION_STATUSES = [
    'new',
    'decoding',
    'needs_sticker',
    'specced',
    'ready_to_blast',
    'blasted',
    'offers_ready',
    'closed_won',
    'closed_lost',
    'withdrawn',
];
export const DECODE_STATUSES = ['pending', 'vpic_only', 'sticker_pending', 'specced', 'failed'];
export const PRIMARY_SPEC_SOURCES = ['vpic', 'seller_sticker', 'paid_api', 'operator'];
export const TRANSMISSIONS = ['manual', 'pdk', 'unknown'];
export const TRANSMISSION_CONFIDENCES = ['confirmed', 'inferred', 'unknown'];
export const SPEC_SOURCES = ['vpic', 'seller_sticker', 'paid_api', 'operator_merged'];
export const CONFIDENCE_LEVELS = ['high', 'medium', 'low'];
export const OPTION_SOURCES = ['seller_sticker', 'paid_api', 'operator'];
export const CODE_FAMILIES = ['M', 'I', 'other'];
export const OPTION_CATEGORIES = [
    'paint',
    'interior',
    'wheels',
    'suspension',
    'brakes',
    'seats',
    'chrono',
    'packages',
    'exterior',
    'audio',
    'other',
];
export const DECODE_SOURCE_TYPES = [
    'vpic',
    'seller_sticker_image',
    'seller_sticker_ocr',
    'paid_api',
];
export const DECODE_SOURCE_STATUSES = ['ok', 'partial', 'failed'];
export const SUBJECT_TYPES = ['seller', 'dealer_contact'];
/** Consent covers voice too; suppression/messaging are SMS+email only. */
export const CONSENT_CHANNELS = ['sms', 'email', 'voice'];
export const MESSAGE_CHANNELS = ['sms', 'email'];
export const CONSENT_TYPES = ['pewc', 'opt_in', 'implied_business'];
export const CONSENT_ACTIONS = ['granted', 'revoked'];
export const SUPPRESSION_REASONS = ['opt_out', 'bounce', 'complaint', 'manual', 'legal'];
export const SMS_DIRECTIONS = ['outbound', 'inbound'];
export const SMS_STATUSES = [
    'queued',
    'scheduled',
    'sent',
    'delivered',
    'failed',
    'undelivered',
    'received',
];
export const EMAIL_STATUSES = [
    'queued',
    'sent',
    'delivered',
    'bounced',
    'complained',
    'failed',
    'received',
];
export const BLAST_STATUSES = ['draft', 'matching', 'pending_send', 'sending', 'sent', 'closed'];
export const BLAST_CHANNELS = ['sms', 'email', 'both'];
export const RECIPIENT_SEND_STATUSES = [
    'pending',
    // M14/M4 — the transient ATOMIC-CLAIM state during fan-out (UPDATE pending→sending RETURNING),
    // so two concurrent sends can never double-send one recipient. Resolves to a terminal status below.
    'sending',
    'consent_blocked',
    'suppressed',
    'nj_blocked',
    'window_deferred',
    'scheduled',
    'sent',
    'delivered',
    'failed',
    'skipped',
];
export const RESPONSE_TYPES = ['ignored', 'bid', 'no_bid'];
export const BID_ORIGINS = ['blast', 'auction', 'direct'];
export const BID_TYPES = ['cash', 'trade', 'cash_plus_trade', 'no_bid', 'pass'];
export const PASS_REASONS = [
    'price_too_high',
    'no_buyer',
    'wrong_spec',
    'mileage',
    'condition',
    'location',
    'timing',
    'has_similar_inventory',
    'other',
];
export const BID_STATUSES = ['active', 'withdrawn', 'superseded', 'won', 'lost'];
export const BID_SOURCES = [
    'sms_parsed',
    'sms_operator_entered',
    'email',
    'phone_logged',
    'web_form',
];
export const OFFER_STATUSES = ['draft', 'presented', 'accepted', 'declined', 'expired', 'withdrawn'];
export const SELLER_RESPONSES = ['accepted', 'declined', 'counter', 'none'];
export const DISCLOSURE_STATES = ['masked', 'dealer_revealed', 'seller_revealed'];
export const REVEAL_TYPES = ['seller_contact_to_dealer', 'dealer_identity_to_seller'];
export const TRIGGER_REASONS = ['formal_bid', 'offer_accepted', 'operator_manual'];
export const TIER_CONTEXTS = ['tier1_leadgen', 'tier3_partner', 'tier3_principal', 'tier2_auction'];
export const DEAL_STATUSES = [
    'matched',
    'intro_made',
    'in_progress',
    'closed_won',
    'closed_lost',
    'fell_through',
];
export const DEAL_OUTCOMES = ['sold', 'no_sale', 'went_elsewhere', 'withdrawn'];
// --- M12 close-out + learning loop -----------------------------------------
/**
 * STRUCTURED fallout taxonomy for a deal that does NOT close (work/launch-council.md §5). Applies when
 * status ∈ (closed_lost, fell_through) / outcome ∈ (no_sale, went_elsewhere, withdrawn). The free-text
 * `deals.outcome_reason` is KEPT alongside it (enum for aggregation; note for nuance) — so a FAILED
 * deal #1 still yields reusable, tallyable learning ("Deal #1 produces reusable data even if it fails").
 */
export const FALLOUT_REASONS = [
    'seller_wanted_more',
    'sold_elsewhere',
    'dealer_backed_out',
    'inspection_failed',
    'title_lien_issue',
    'seller_changed_mind',
    'financing',
    'logistics',
    'price_gap',
    'other',
];
/** Which side a per-deal NPS/feedback row (or a testimonial) came from. Both sides are captured. */
export const FEEDBACK_PARTIES = ['seller', 'dealer'];
/** Whether a consented testimonial/case-study quote may be attributed by name, or must stay anonymous. */
export const TESTIMONIAL_ATTRIBUTIONS = ['attributed', 'anonymous'];
export const COMP_TYPES = [
    'dealer_bid',
    'dealer_pass_signal',
    'realized_sale',
    'external_listing',
    'external_auction',
];
export const PRICE_KINDS = [
    'cash_bid',
    'trade_adjusted',
    'realized_wholesale',
    'realized_retail',
    'asking',
    'sold',
];
export const COMP_SOURCES = [
    'motoveo_bid',
    'motoveo_deal',
    'bring_a_trailer',
    'carsandbids',
    'classic_com',
    'manheim',
    'operator_manual',
];
// --- M11 founder valuation / appraisal worksheet ---------------------------
/** The comp types a founder may ENTER by hand/CSV — external retail observations only (never a moat bid). */
export const EXTERNAL_COMP_TYPES = ['external_listing', 'external_auction'];
/** The comp sources a founder may attribute a hand/CSV comp to (the marketplaces + a manual catch-all). */
export const EXTERNAL_COMP_SOURCES = [
    'bring_a_trailer',
    'carsandbids',
    'classic_com',
    'manheim',
    'operator_manual',
];
/** Did the comp SELL (a realized price) or is it a LISTING (an asking price)? Drives the listing discount. */
export const COMP_PRICE_KINDS = ['asking', 'sold', 'realized_retail'];
/**
 * The appraisal verdict: is the seller's number within a plausible dealer-bid range?
 *   - closable    seller anchor is within the estimated dealer-acquisition band.
 *   - marginal    anchor is just above the band high (within tolerance) — a stretch, worth a call.
 *   - unrealistic anchor exceeds the band beyond tolerance — not launchable at that number.
 *   - no_anchor   the band exists but no seller floor/asking was captured.
 *   - no_comps    not enough relevant comps to form a band yet.
 */
export const VALUATION_VERDICTS = ['closable', 'marginal', 'unrealistic', 'no_anchor', 'no_comps'];
/** How the estimated dealer-acquisition band was derived (transparent provenance for the worksheet). */
export const DEALER_ACQ_SOURCES = ['retail_margin', 'internal_dealer_comps'];
/** Which seller number the verdict was measured against (floor is preferred; asking is the fallback). */
export const VALUATION_ANCHOR_SOURCES = ['seller_floor', 'asking_price'];
export const AUDIT_ACTOR_TYPES = ['operator', 'system', 'dealer', 'seller', 'webhook'];
export const MEDIA_OWNER_TYPES = [
    'seller_submission',
    'vehicle',
    'decode_source',
    'consent',
    'sms_message',
];
export const MEDIA_KINDS = ['photo', 'window_sticker', 'consent_evidence', 'other'];
export const REVIEW_TASK_KINDS = [
    'ambiguous_bid_review',
    'unmapped_option_code',
    'spec_verify',
    'sticker_needed',
    'consent_followup',
    'other',
];
export const REVIEW_TASK_STATUSES = ['open', 'in_progress', 'done', 'dismissed'];
export const REVIEW_TASK_PRIORITIES = ['low', 'normal', 'high'];
export const MAGIC_LINK_PURPOSES = [
    'seller_sticker_upload',
    'dealer_bid',
    'unsubscribe',
    'offer_view',
];
/** Derived effective-consent state (from v_current_consent + suppression). */
export const CONSENT_STATES = ['granted', 'revoked', 'none'];
/** Purpose of an outbound message — drives the NJ advertising-only gate. */
export const SEND_PURPOSES = ['marketing', 'transactional', 'opt_out_confirm'];
//# sourceMappingURL=enums.js.map